<template>
  <div class="subscriptions-page">

    <!-- ================= HEADER ================= -->
    <div class="page-header">

      <div>
        <h1>Plans</h1>
        <p>Create and manage subscription plans.</p>
      </div>

      <div class="header-actions">
        <button class="primary-btn" @click="openCreate">
          <span class="material-symbols-outlined">add</span>
          Add Plan
        </button>
      </div>

    </div>

    <!-- ================= TABLE ================= -->
    <div class="table-card">

      <table>
        <thead>
          <tr>
            <th>Plan</th>
            <th>Price</th>
            <th>Duration</th>
            <th>Review Limit</th>
            <th>Campaign Limit</th>
            <th>Status</th>
            <th class="right">Actions</th>
          </tr>
        </thead>

        <tbody>

          <tr v-if="loading">
            <td colspan="7" class="muted center">Loading plans...</td>
          </tr>

          <tr v-else-if="plans.length === 0">
            <td colspan="7" class="muted center">No plans found</td>
          </tr>

          <tr v-for="plan in plans" :key="plan.plan_id">

            <td>
              <strong>{{ plan.plan_name }}</strong>
              <div class="biz-meta">{{ plan.plan_type }}</div>
            </td>

            <td>
              <strong class="revenue">${{ plan.plan_price }}</strong>
            </td>

            <td class="muted">{{ plan.plan_duration }}</td>
            <td class="muted">{{ plan.plan_review_limit }}</td>
            <td class="muted">{{ plan.plan_camp_limit }}</td>

            <td>
              <span :class="['status-pill', plan.plan_status]">
                {{ plan.plan_status }}
              </span>
            </td>

            <td class="right actions-cell">

              <div class="actions-inline">

                <button class="icon-btn" @click="editPlan(plan)">
                  <span class="material-symbols-outlined">edit</span>
                </button>

                <button class="mini-btn" @click="toggleMenu(plan.plan_id)">
                  <span class="material-symbols-outlined">more_vert</span>
                </button>

              </div>

              <div v-if="activeMenu === plan.plan_id" class="actions-menu">
                <div @click="editPlan(plan)">
                  <span class="material-symbols-outlined">edit</span>Edit
                </div>

                <div class="danger" @click="removePlan(plan.plan_id)">
                  <span class="material-symbols-outlined">delete</span>Delete
                </div>
              </div>

            </td>

          </tr>

        </tbody>
      </table>

      <!-- ================= PAGINATION ================= -->

      <div class="pagination">

        Page {{ page + 1 }}

        <div class="pages">
          <button :disabled="page === 0" @click="prevPage">
            <span class="material-symbols-outlined">chevron_left</span>
          </button>

          <button :disabled="plans.length === 0" @click="nextPage">
            <span class="material-symbols-outlined">chevron_right</span>
          </button>
        </div>

      </div>

    </div>

    <!-- ================= PLAN MODAL ================= -->

    <div v-if="openPlanModal" class="modal-overlay">

      <div class="plan-modal">

        <div class="modal-header">
          <h3>{{ editing ? "Edit Plan" : "Create Plan" }}</h3>

          <button class="close-btn" @click="closeModal">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>

        <div class="modal-body">

          <div class="form-group">
            <label>Plan Name</label>
            <input v-model="form.plan_name" />
          </div>

          <div class="form-group">
            <label>Price</label>
            <input v-model="form.plan_price" />
          </div>

          <div class="form-group">
            <label>Duration</label>
            <input v-model="form.plan_duration" />
          </div>

          <div class="form-group">
            <label>Review Limit</label>
            <input v-model="form.plan_review_limit" />
          </div>

          <div class="form-group">
            <label>Campaign Limit</label>
            <input v-model="form.plan_camp_limit" />
          </div>

          <div class="form-group">
            <label>Status</label>
            <select v-model="form.plan_status">
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </div>

          <div class="form-group">
            <label>Features</label>

            <div class="features-editor">

              <div
                v-for="(feature, index) in form.features"
                :key="index"
                class="feature-row"
              >
                <input v-model="form.features[index]" placeholder="Enter feature" />

                <button class="remove-feature" @click="removeFeature(index)">
                  <span class="material-symbols-outlined">close</span>
                </button>
              </div>

              <button class="add-feature-btn" @click="addFeature">
                <span class="material-symbols-outlined">add</span>
                Add Feature
              </button>

            </div>
          </div>

          <div class="modal-actions">
            <button class="ghost-btn" @click="closeModal">Cancel</button>
            <button class="primary-btn" @click="save">Save Plan</button>
          </div>

        </div>

      </div>
    </div>

  </div>
</template>

<script>
import { getPlanList, savePlan, deletePlan } from "@/apis/planapis";

export default {
  name: "AdminPlans",

  data() {
    return {
      plans: [],
      activeMenu: null,
      openPlanModal: false,
      editing: false,
      page: 0,
      loading: false,

      form: {
        plan_id: "",
        plan_name: "",
        plan_price: "",
        plan_duration: "",
        plan_review_limit: "",
        plan_camp_limit: "",
        plan_status: "active",
        features: [], // ✅ NEW
      },
    };
  },

  mounted() {
    this.loadPlans();
  },

  methods: {
    addFeature() {
      this.form.features.push("");
    },

    removeFeature(index) {
      this.form.features.splice(index, 1);
    },
    async loadPlans() {
      this.loading = true;

      const res = await getPlanList("", "", this.page.toString());

      if (res.status) {
        this.plans = res.data || [];
      }

      this.loading = false;
    },

    nextPage() {
      if (this.plans.length === 0) return;

      this.page++;
      this.loadPlans();
    },

    prevPage() {
      if (this.page === 0) return;

      this.page--;
      this.loadPlans();
    },

    toggleMenu(id) {
      this.activeMenu = this.activeMenu === id ? null : id;
    },

    openCreate() {
      this.editing = false;
      this.resetForm();
      this.openPlanModal = true;
    },

    editPlan(plan) {
      this.editing = true;

      this.form = {
        ...plan,
        features: plan.plan_features
          ? JSON.parse(plan.plan_features)
          : [],
      };

      this.openPlanModal = true;
      this.activeMenu = null;
    },

    closeModal() {
      this.openPlanModal = false;
      this.resetForm();
    },

    resetForm() {
      this.form = {
        plan_id: "",
        plan_name: "",
        plan_price: "",
        plan_duration: "",
        plan_review_limit: "",
        plan_camp_limit: "",
        plan_status: "active",
        features: [], // ✅ NEW
      };
    },

    async save() {
      if (!this.form.plan_name) return;

      await savePlan(
        this.form.plan_id,
        this.form.plan_name,
        "",
        "",
        this.form.plan_price,
        "",
        this.form.plan_duration,
        this.form.plan_review_limit,
        this.form.plan_camp_limit,
        JSON.stringify(this.form.features), // ✅ IMPORTANT
        this.form.plan_status
      );

      this.closeModal();
      this.loadPlans();
    },

    async removePlan(id) {
      await deletePlan(id);
      this.activeMenu = null;
      this.loadPlans();
    },
  },
};
</script>


<style scoped>
/* ================= PAGE ================= */

.subscriptions-page {
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  background: #f6f7f8;
  min-height: 100vh;
}

/* ================= HEADER ================= */

.page-header {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.page-header h1 {
  font-size: 28px;
  font-weight: 800;
  color: #0f172a;
}

.page-header p {
  font-size: 14px;
  color: #64748b;
  margin-top: 4px;
}

/* ================= ACTIONS ================= */

.header-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

/* ================= SEARCH ================= */

.search-box {
  height: 40px;
  background: white;
  border-radius: 12px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #e2e8f0;
  min-width: 240px;
}

.search-box span {
  font-size: 18px;
  color: #94a3b8;
}

.search-box input {
  border: none;
  outline: none;
  font-size: 13px;
  width: 100%;
  background: transparent;
}

/* ================= BUTTONS ================= */

.primary-btn {
  height: 40px;
  padding: 0 16px;
  background: #197fe6;
  border-radius: 12px;
  color: white;
  font-size: 13px;
  font-weight: 600;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: 0.15s;
}

.primary-btn:hover {
  background: #166fd1;
}

.filter-btn {
  height: 40px;
  padding: 0 14px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  transition: 0.15s;
}

.filter-btn:hover {
  background: #f8fafc;
}

/* ================= METRICS ================= */

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.metric-card {
  background: white;
  border-radius: 16px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 6px 18px rgba(0,0,0,0.04);
}

.metric-card span {
  font-size: 12px;
  color: #64748b;
}

.metric-card strong {
  font-size: 22px;
  display: block;
  margin-top: 6px;
  color: #0f172a;
}

.metric-card small {
  font-size: 11px;
}

/* ================= TABLE CARD ================= */

.table-card {
  background: white;
  border-radius: 18px;
  border: 1px solid #e2e8f0;
  overflow-x: auto;
  box-shadow: 0 10px 30px rgba(0,0,0,0.04);
}

/* ================= TABLE ================= */

table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1050px;
}

thead {
  background: #f8fafc;
}

th {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-weight: 700;
  color: #94a3b8;
  padding: 14px 18px;
  text-align: left;
}

td {
  padding: 16px 18px;
  border-top: 1px solid #f1f5f9;
  font-size: 13px;
  color: #0f172a;
  vertical-align: middle;
}

tbody tr:hover {
  background: #f8fafc;
}

/* ================= BUSINESS CELL ================= */

.business-cell {
  display: flex;
  gap: 10px;
  align-items: center;
}

.biz-avatar {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: #197fe6;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.biz-name {
  font-weight: 600;
  font-size: 13px;
}

.biz-meta {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
}

/* ================= BADGES ================= */

.plan-badge {
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.plan-badge.pro {
  background: #eef2ff;
  color: #4f46e5;
}

.plan-badge.starter {
  background: #f1f5f9;
  color: #64748b;
}

/* ================= STATUS ================= */

.status-pill {
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.status-pill.active {
  background: #dcfce7;
  color: #166534;
}

.status-pill.trial {
  background: #fef9c3;
  color: #854d0e;
}

/* ================= ACTIONS ================= */

.actions-cell {
  position: relative;
}

.actions-inline {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.icon-btn,
.mini-btn {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.15s;
}

.icon-btn span,
.mini-btn span {
  font-size: 18px;
  color: #64748b;
}

.icon-btn:hover,
.mini-btn:hover {
  background: #f1f5f9;
}

.icon-btn:hover span {
  color: #197fe6;
}

/* ================= DROPDOWN MENU ================= */

.actions-menu {
  position: absolute;
  top: 42px;
  right: 0;
  background: white;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 20px 50px rgba(0,0,0,0.12);
  width: 170px;
  padding: 6px;
  z-index: 50;
  animation: menuEnter 0.12s ease;
}

.actions-menu div {
  padding: 9px 10px;
  border-radius: 10px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #334155;
  transition: 0.12s;
}

.actions-menu div span {
  font-size: 18px;
  color: #94a3b8;
}

.actions-menu div:hover {
  background: #f8fafc;
}

.actions-menu div:hover span {
  color: #197fe6;
}

.actions-menu .danger {
  color: #dc2626;
}

.actions-menu .danger:hover {
  background: #fef2f2;
}

/* ================= PAGINATION ================= */

.pagination {
  padding: 16px 18px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #64748b;
  flex-wrap: wrap;
  gap: 10px;
}

.pages {
  display: flex;
  gap: 6px;
}

.pages button {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: white;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.pages button.active {
  background: #197fe6;
  color: white;
  border-color: #197fe6;
}

.pages button:disabled {
  opacity: 0.4;
  cursor: default;
}

/* ================= HELPERS ================= */

.muted { color: #94a3b8; }
.revenue { color: #197fe6; }
.positive { color: #16a34a; }
.warning { color: #f59e0b; }
.neutral { color: #94a3b8; }

.right {
  text-align: right;
}

/* ================= ANIMATION ================= */

@keyframes menuEnter {
  from {
    transform: translateY(4px);
    opacity: 0;
  }
  to {
    transform: none;
    opacity: 1;
  }
}

/* ================= MOBILE ================= */

@media (max-width: 768px) {

  .subscriptions-page {
    padding: 16px;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
  }

  .search-box {
    width: 100%;
  }

}
/* ================= MODAL OVERLAY ================= */

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

/* ================= MODAL CARD ================= */

.plan-modal {
  width: 520px;
  max-width: 95%;
  background: white;
  border-radius: 18px;
  padding: 22px;
  box-shadow: 0 25px 60px rgba(0,0,0,0.18);
  animation: modalEnter 0.18s ease;
}

/* ================= MODAL HEADER ================= */

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
}

.modal-header h3 {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

/* ================= CLOSE BUTTON ================= */

.close-btn {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: none;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.2s;
}

.close-btn span {
  font-size: 18px;
  color: #64748b;
}

.close-btn:hover {
  background: #e2e8f0;
}

.close-btn:hover span {
  color: #0f172a;
}

/* ================= MODAL BODY ================= */

.modal-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* ================= FORM GROUP ================= */

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

/* ================= INPUTS ================= */

.form-group input,
.form-group select {
  height: 42px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  padding: 0 12px;
  font-size: 13px;
  outline: none;
  transition: 0.2s;
  background: white;
}

.form-group input:focus,
.form-group select:focus {
  border-color: #197fe6;
  box-shadow: 0 0 0 3px rgba(25,127,230,0.12);
}

/* ================= SELECT POLISH ================= */

.form-group select {
  appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, #94a3b8 50%),
                    linear-gradient(135deg, #94a3b8 50%, transparent 50%);
  background-position: calc(100% - 18px) calc(50% - 3px),
                       calc(100% - 12px) calc(50% - 3px);
  background-size: 6px 6px, 6px 6px;
  background-repeat: no-repeat;
  cursor: pointer;
}

/* ================= FEATURES GRID ================= */

.features-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 6px;
}

/* ================= FEATURE TOGGLE ================= */

.feature-toggle {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px;
  display: flex;
  gap: 10px;
  cursor: pointer;
  transition: 0.15s;
  background: white;
}

.feature-toggle span {
  font-size: 20px;
  color: #94a3b8;
}

.feature-toggle strong {
  font-size: 13px;
  font-weight: 600;
}

.feature-toggle small {
  font-size: 11px;
  color: #94a3b8;
}

.feature-toggle:hover {
  background: #f8fafc;
}

.feature-toggle.active {
  border-color: #197fe6;
  background: #eff6ff;
}

.feature-toggle.active span {
  color: #197fe6;
}

/* ================= MODAL ACTIONS ================= */

.modal-actions {
  display: flex;
  gap: 10px;
  margin-top: 12px;
}

.modal-actions button {
  flex: 1;
}

/* ================= BUTTON CONSISTENCY ================= */

.primary-btn,
.ghost-btn {
  justify-content: center;
  white-space: nowrap;
}

.primary-btn.small,
.ghost-btn.small {
  height: 32px;
  font-size: 12px;
  padding: 0 10px;
}

/* ================= TABLE FIXES ================= */

.table-card {
  position: relative;
}

.actions-inline {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
}

/* ================= DROPDOWN MENU FIX ================= */

.actions-menu {
  position: absolute;
  top: 42px;
  right: 0;
  background: white;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 20px 50px rgba(0,0,0,0.12);
  width: 170px;
  padding: 6px;
  z-index: 50;
  animation: menuEnter 0.12s ease;
}

.actions-menu div {
  padding: 9px 10px;
  border-radius: 10px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #334155;
  transition: 0.12s;
}

.actions-menu div span {
  font-size: 18px;
  color: #94a3b8;
}

.actions-menu div:hover {
  background: #f8fafc;
}

.actions-menu div:hover span {
  color: #197fe6;
}

.actions-menu .danger {
  color: #dc2626;
}

.actions-menu .danger:hover {
  background: #fef2f2;
}

/* ================= OVERFLOW SAFETY ================= */

.subscriptions-page,
.table-card,
.chart-wrapper {
  min-width: 0;
}

/* ================= ANIMATIONS ================= */

@keyframes modalEnter {
  from {
    transform: translateY(6px) scale(0.98);
    opacity: 0;
  }
  to {
    transform: none;
    opacity: 1;
  }
}

@keyframes menuEnter {
  from {
    transform: translateY(4px);
    opacity: 0;
  }
  to {
    transform: none;
    opacity: 1;
  }
}

/* ================= MOBILE ================= */

@media (max-width: 768px) {

  .plan-modal {
    width: 100%;
    border-radius: 16px;
    padding: 18px;
  }

  .features-grid {
    grid-template-columns: 1fr;
  }

  .modal-actions {
    flex-direction: column;
  }

}

/* ================= HELPERS ================= */

.center { text-align: center; }

/* ================= STATUS COLORS ================= */

.status-pill.active {
  background: #dcfce7;
  color: #166534;
}

.status-pill.inactive {
  background: #fee2e2;
  color: #991b1b;
}

/* ================= PAGINATION ================= */

.pagination {
  padding: 16px 18px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #64748b;
}

.pages {
  display: flex;
  gap: 6px;
}

.pages button:disabled {
  opacity: 0.4;
  cursor: default;
}

/* ✅ KEEP REST OF YOUR ORIGINAL CSS */
/* No need to duplicate entire style */
.features-editor {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.feature-row {
  display: flex;
  gap: 6px;
}

.feature-row input {
  flex: 1;
}

.remove-feature {
  width: 42px;
  border: none;
  background: #f1f5f9;
  border-radius: 10px;
  cursor: pointer;
}

.remove-feature span {
  font-size: 18px;
  color: #64748b;
}

.add-feature-btn {
  height: 38px;
  border-radius: 10px;
  border: 1px dashed #cbd5f5;
  background: #f8fafc;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
}
</style>